import {FocusMonitor} from '@angular/cdk/a11y';
import {isPlatformBrowser} from '@angular/common';
import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import {MatIcon} from '@angular/material/icon';
import {MatListItem, MatNavList} from '@angular/material/list';
import {NavigationEnd, Router, RouterLink} from '@angular/router';
import {InstanceUIConfigResponse, UiConfigService} from '@middleware';
import {MOBILE_NAV_BREAKPOINT} from '@shared/constants/constants';
import {filter} from 'rxjs/operators';

type InstanceUIConfigSchema = InstanceUIConfigResponse['config_data'];

export interface NavRailItem {
  name: string;
  routerLink: string;
  icon: string;
  active: () => boolean;
}

export interface RouteData {
  firstURLSegment: string;
}

@Component({
  selector: 'nav.app-navrail',
  imports: [MatNavList, MatIcon, MatListItem, RouterLink],
  templateUrl: './navrail.component.html',
  styleUrl: './navrail.component.scss',
  host: {
    '[class.expanded]': 'this.expanded',
    '(mouseleave)': 'onNavMouseLeave($event)',
  },
})
export class NavrailComponent {
  @ViewChild('navtoggle') navtoggle!: MatListItem;

  private readonly _allNavRailItems: NavRailItem[] = [
    {
      name: 'Home',
      icon: 'home',
      routerLink: '/',
      active: () =>
        this.router.isActive('/', {
          paths: 'exact',
          queryParams: 'ignored',
          matrixParams: 'ignored',
          fragment: 'ignored',
        }),
    },
    {
      name: 'Resource Explorer',
      icon: 'map_search',
      routerLink: '/resource-explorer',
      active: () =>
        this.router.isActive('/resource-explorer', {
          paths: 'subset',
          queryParams: 'ignored',
          matrixParams: 'ignored',
          fragment: 'ignored',
        }),
    },
    {
      name: 'Private Data Enclave',
      icon: 'security',
      routerLink: '/private-data-enclave',
      active: () =>
        this.router.isActive('/private-data-enclave', {
          paths: 'subset',
          queryParams: 'ignored',
          matrixParams: 'ignored',
          fragment: 'ignored',
        }),
    },
    {
      name: 'Public Data Commons',
      icon: 'search_insights',
      routerLink: '/public-data-commons',
      active: () =>
        this.router.isActive('/public-data-commons', {
          paths: 'subset',
          queryParams: 'ignored',
          matrixParams: 'ignored',
          fragment: 'ignored',
        }),
    },
    {
      name: 'Help',
      icon: 'help',
      routerLink: '/help',
      active: () =>
        this.router.isActive('/help', {
          paths: 'subset',
          queryParams: 'ignored',
          matrixParams: 'ignored',
          fragment: 'ignored',
        }),
    },
  ];

  public manuallyExpanded = false;
  private mouseDown = false;
  private keyboardInside = false;
  configData = signal<InstanceUIConfigSchema | undefined>(undefined);

  protected get expanded() {
    let isMobile = false;
    if (isPlatformBrowser(this.platformId)) {
      isMobile = window.innerWidth <= MOBILE_NAV_BREAKPOINT;
    }

    if (isMobile) {
      return this.manuallyExpanded;
    }

    // On desktop, use this combined logic to determine whether to expand
    return this.manuallyExpanded || this.mouseDown || this.keyboardInside;
  }

  toggle() {
    this.manuallyExpanded = !this.manuallyExpanded;
  }

  onNavMouseLeave(event: MouseEvent): void {
    const nav = event.currentTarget as HTMLElement;
    const focused = nav.querySelector<HTMLElement>(':focus');
    focused?.blur();
  }

  /**
   * Handles collapse when clicking outside the navrail.
   */
  @HostListener('document:click', ['$event.target'])
  clickout(targetElement: any): void {
    let isMobile = false;
    if (isPlatformBrowser(this.platformId)) {
      isMobile = window.innerWidth <= MOBILE_NAV_BREAKPOINT;
    }

    if (isMobile) {
      return;
    }

    if (!this.manuallyExpanded) {
      return;
    }

    const clickedInside = this.elementRef.nativeElement.contains(targetElement);

    if (!clickedInside) {
      setTimeout(() => {
        this.manuallyExpanded = false;
        this.changeDetector.markForCheck();
      }, 0);
    }
  }

  get navRailItems(): NavRailItem[] {
    const config = this.configData();

    if (!config) {
      return this._allNavRailItems.filter(item => item.routerLink === '/' || item.routerLink.startsWith('/help'));
    }

    return this._allNavRailItems.filter(item => {
      // Items not tied to a module (e.g. Home, Help) are included by default.
      if (item.routerLink === '/' || item.routerLink.startsWith('/help')) {
        return true;
      }

      // Derive the slug from the routerLink for module-based items.
      const slug = item.routerLink.substring(1).replace(/-/g, '_');

      const module = config[slug];
      return module?.enabled === true;
    });
  }

  constructor(
    private readonly router: Router,
    private readonly elementRef: ElementRef,
    private readonly focusMonitor: FocusMonitor,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly uiConfigService: UiConfigService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
  ) {
    this.uiConfigService.getInstanceUIConfig().subscribe(v => this.configData.set(v.config_data));

    // Manually re-evaluate the selected nav-item after navigation.
    // The active state is derived from Router.isActive() during change detection.
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.changeDetector.markForCheck();
    });

    afterNextRender({
      read: () => {
        const element = this.elementRef.nativeElement as HTMLElement;
        const self = this;

        element.addEventListener('mousedown', _ => {
          self.mouseDown = true;
          changeDetector.markForCheck();
        });
        element.addEventListener('mouseleave', _ => {
          self.mouseDown = false;
          changeDetector.markForCheck();
        });
        element.addEventListener('click', _ => {
          self.mouseDown = true;
          changeDetector.markForCheck();
        });

        this.focusMonitor.monitor(element, true).subscribe(origin => {
          this.keyboardInside = origin === 'keyboard';
          changeDetector.markForCheck();
        });
      },
    });
  }
}
